import { useState, useEffect, useMemo } from 'react'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import {
  api,
  getSessionCookieToken,
  type HealthStatus,
  type IntegrationCatalogEntry,
  type IntegrationField,
} from '../api/client'
import { IntegrationLogo } from '../components/IntegrationLogo'
import { ByokSettings } from '../components/ByokSettings'
import { BitbucketAppCard } from '../components/BitbucketAppCard'
import { GitHubAppCard } from '../components/GitHubAppCard'
import { SentryProjectRepoMap } from '../components/SentryProjectRepoMap'
import { SentryWebhookSetup } from '../components/SentryWebhookSetup'
import { VercelWebhookSetup } from '../components/VercelWebhookSetup'
import LoadingOverlay from '../components/LoadingOverlay'

const CONNECTION_METHOD_LABELS: Record<string, string> = {
  api_key: 'API Key',
  oauth: 'OAuth',
  cli: 'CLI',
  direct_connect: 'Direct Connect',
}

type VerifyState = { status: string; message: string } | null

function ConnectionMethodBadge({ method }: { method: string }) {
  const cls = `conn-type-badge conn-type-${method.replace('_', '-')}`
  return <span className={cls}>{CONNECTION_METHOD_LABELS[method] ?? method}</span>
}

function VerifyButton({
  entry,
  onVerified,
}: {
  entry: IntegrationCatalogEntry
  onVerified: (s: VerifyState) => void
}) {
  const { getToken } = useAuth()
  const [verifying, setVerifying] = useState(false)

  async function handleVerify() {
    setVerifying(true)
    try {
      const token = getSessionCookieToken() ?? (await getToken())
      if (!token) throw new Error('Not authenticated')
      const result = await api.verifyIntegrationHealth(token, entry.id)
      onVerified({ status: result.status, message: result.message })
    } catch (err) {
      onVerified({ status: 'error', message: (err as Error).message || 'Verification failed.' })
    } finally {
      setVerifying(false)
    }
  }

  return (
    <button type="button" className="verify-btn" onClick={handleVerify} disabled={verifying}>
      {verifying ? 'Checking…' : 'Verify'}
    </button>
  )
}

const INTEGRATION_SCOPES: Record<string, { label: string; style: React.CSSProperties }> = {
  datadog: { label: 'READ-ONLY (Metrics/Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  grafana: { label: 'READ-ONLY (Metrics/Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  honeycomb: { label: 'READ-ONLY (Metrics/Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  sentry: { label: 'READ-ONLY (Exceptions)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  signoz: { label: 'READ-ONLY (Traces)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  coralogix: { label: 'READ-ONLY (Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  betterstack: { label: 'READ-ONLY (Status)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  splunk: { label: 'READ-ONLY (Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  opensearch: { label: 'READ-ONLY (Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  openobserve: { label: 'READ-ONLY (Logs)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  azure_monitor: { label: 'READ-ONLY (Telemetry)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  alertmanager: { label: 'READ-ONLY (Alerts Ingest)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  kubernetes: { label: 'READ-ONLY (Cluster State)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  aws: { label: 'READ-ONLY (IAM/CloudWatch)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  slack: { label: 'WRITE-ONLY (Alarms)', style: { background: 'rgba(90, 176, 212, 0.08)', color: 'var(--info)', border: '1px solid rgba(90, 176, 212, 0.2)' } },
  discord: { label: 'WRITE-ONLY (Alarms)', style: { background: 'rgba(90, 176, 212, 0.08)', color: 'var(--info)', border: '1px solid rgba(90, 176, 212, 0.2)' } },
  telegram: { label: 'WRITE-ONLY (Alarms)', style: { background: 'rgba(90, 176, 212, 0.08)', color: 'var(--info)', border: '1px solid rgba(90, 176, 212, 0.2)' } },
  pagerduty: { label: 'READ-ONLY (Alerts Ingest)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  opsgenie: { label: 'READ-ONLY (Alerts Ingest)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  incident_io: { label: 'READ-ONLY (Alerts Ingest)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  jira: { label: 'WRITE-ONLY (Issues)', style: { background: 'rgba(90, 176, 212, 0.08)', color: 'var(--info)', border: '1px solid rgba(90, 176, 212, 0.2)' } },
  github: { label: 'WRITE (Branches/PRs)', style: { background: 'rgba(232, 165, 90, 0.08)', color: 'var(--warning)', border: '1px solid rgba(232, 165, 90, 0.2)' } },
  bitbucket: { label: 'READ-ONLY (Repos)', style: { background: 'rgba(93, 184, 114, 0.08)', color: 'var(--success)', border: '1px solid rgba(93, 184, 114, 0.2)' } },
  gitlab: { label: 'WRITE (Branches/PRs)', style: { background: 'rgba(232, 165, 90, 0.08)', color: 'var(--warning)', border: '1px solid rgba(232, 165, 90, 0.2)' } },
  vercel: { label: 'WRITE (PR Comments)', style: { background: 'rgba(232, 165, 90, 0.08)', color: 'var(--warning)', border: '1px solid rgba(232, 165, 90, 0.2)' } },
}

function HealthBadge({ status }: { status: HealthStatus | undefined }) {
  if (!status || status === 'missing') {
    return <span className="health-badge health-missing">Not configured</span>
  }
  if (status === 'ok') {
    return <span className="health-badge health-ok">Connected</span>
  }
  return <span className="health-badge health-error">Auth error</span>
}

type SaasCardProps = {
  entry: IntegrationCatalogEntry
  health: HealthStatus | undefined
  onSaved: () => void
}

function SaasIntegrationCard({ entry, health, onSaved }: SaasCardProps) {
  const { getToken } = useAuth()
  const fields = entry.fields
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, ''])),
  )
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)
  const [webhookRefresh, setWebhookRefresh] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [verifyResult, setVerifyResult] = useState<VerifyState>(null)

  function handleChange(key: string, val: string) {
    setValues((prev) => ({ ...prev, [key]: val }))
    setSaveError(null)
    setSaveOk(false)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const filled = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v.trim() !== ''),
    )
    if (Object.keys(filled).length === 0) {
      setSaveError('Enter at least one field before saving.')
      return
    }
    setSaving(true)
    setSaveError(null)
    setSaveOk(false)
    try {
      const token = getSessionCookieToken() ?? (await getToken())
      if (!token) throw new Error('Not authenticated')
      await api.upsertCredentials(token, entry.id, filled)
      setSaveOk(true)
      setValues(Object.fromEntries(fields.map((f) => [f.key, ''])))
      if (entry.id === 'sentry' || entry.id === 'vercel') {
        setWebhookRefresh((n) => n + 1)
      }
      onSaved()
    } catch (err) {
      setSaveError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const isOAuth = entry.connection_method === 'oauth'
  const scope = INTEGRATION_SCOPES[entry.id]

  return (
    <div className={`cred-card ${expanded ? 'cred-card--open' : ''}`} style={{ marginBottom: '8px' }}>
      <button
        type="button"
        className="cred-card-header"
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <span className="cred-card-icon" style={{ display: 'flex', alignItems: 'center' }}>
          <IntegrationLogo integrationId={entry.id} size={22} />
        </span>
        <span className="cred-card-title" style={{ fontWeight: 600 }}>{entry.display_name}</span>
        
        {/* Capabilities tag */}
        {scope && (
          <span className="conn-type-badge" style={scope.style}>
            {scope.label}
          </span>
        )}

        <ConnectionMethodBadge method={entry.connection_method} />
        <HealthBadge status={health} />
        <span className="cred-card-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="cred-card-body">
          <div className="cred-card-toolbar" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <VerifyButton entry={entry} onVerified={setVerifyResult} />
          </div>

          {verifyResult && (
            <p className={`verify-result verify-result--${verifyResult.status}`} style={{ margin: '8px 0 0' }}>
              {verifyResult.message}
            </p>
          )}

          {isOAuth ? (
            <div className="oauth-section" style={{ marginTop: '12px' }}>
              <p className="oauth-description" style={{ color: 'var(--text)', fontSize: '13px' }}>
                Click below to connect {entry.display_name} via OAuth.
              </p>
              <a
                className="oauth-connect-btn"
                href={`/api/v1/oauth/${entry.id}/connect`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: '8px' }}
              >
                Authorize {entry.display_name}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
              {fields.map((field: IntegrationField) => (
                <div className="cred-field" key={field.key}>
                  <label className="cred-label" htmlFor={`${entry.id}-${field.key}`}>
                    {field.label}
                  </label>
                  {field.key === 'KUBERNETES_KUBECONFIG' ? (
                    <textarea
                      id={`${entry.id}-${field.key}`}
                      className="cred-input cred-textarea"
                      value={values[field.key]}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder ?? ''}
                      rows={4}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  ) : (
                    <input
                      id={`${entry.id}-${field.key}`}
                      className="cred-input"
                      type={field.secret ? 'password' : 'text'}
                      value={values[field.key]}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.placeholder ?? ''}
                      autoComplete={field.secret ? 'new-password' : 'off'}
                    />
                  )}
                </div>
              ))}

              {saveError && <p className="cred-error">{saveError}</p>}
              {saveOk && <p className="cred-success">Credentials saved.</p>}

              {entry.id === 'sentry' && (
                <>
                  <SentryWebhookSetup
                    getAuthToken={async () => {
                      const token = getSessionCookieToken() ?? (await getToken())
                      if (!token) throw new Error('Not authenticated')
                      return token
                    }}
                    refreshTrigger={webhookRefresh}
                  />
                  <SentryProjectRepoMap
                    getAuthToken={async () => {
                      const token = getSessionCookieToken() ?? (await getToken())
                      if (!token) throw new Error('Not authenticated')
                      return token
                    }}
                    refreshTrigger={webhookRefresh}
                  />
                </>
              )}

              {entry.id === 'vercel' && (
                <VercelWebhookSetup
                  getAuthToken={async () => {
                    const token = getSessionCookieToken() ?? (await getToken())
                    if (!token) throw new Error('Not authenticated')
                    return token
                  }}
                  refreshTrigger={webhookRefresh}
                />
              )}

              <div className="cred-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="submit" className="cred-save-btn" disabled={saving}>
                  {saving ? 'Encrypting & Saving…' : 'Encrypt & Save Credentials'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  )
}

function CliIntegrationRow({ entry }: { entry: IntegrationCatalogEntry }) {
  return (
    <div className="cli-integration-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: '8px' }}>
      <span className="cred-card-icon" style={{ display: 'flex', alignItems: 'center' }}>
        <IntegrationLogo integrationId={entry.id} size={20} />
      </span>
      <span className="cli-integration-name" style={{ flex: 1, fontWeight: 500, color: 'var(--text-h)' }}>
        {entry.display_name}
      </span>
      <span className="cli-integration-badge" style={{ fontSize: '11px', background: 'var(--border)', color: 'var(--text-muted)' }}>Coming soon</span>
    </div>
  )
}


function groupByCategory(entries: IntegrationCatalogEntry[]): Map<string, IntegrationCatalogEntry[]> {
  const map = new Map<string, IntegrationCatalogEntry[]>()
  for (const entry of entries) {
    const category = entry.category || 'Other'
    const list = map.get(category) ?? []
    list.push(entry)
    map.set(category, list)
  }
  return map
}

export default function Settings() {
  const { getToken } = useAuth()
  const { membership } = useOrganization()
  const isAdmin = membership?.role === 'org:admin'
  const [catalog, setCatalog] = useState<IntegrationCatalogEntry[]>([])
  const [cliCatalog, setCliCatalog] = useState<IntegrationCatalogEntry[]>([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [health, setHealth] = useState<Record<string, HealthStatus>>({})
  const [healthLoading, setHealthLoading] = useState(true)
  const [healthError, setHealthError] = useState<string | null>(null)
  const [byokFeatureEnabled, setByokFeatureEnabled] = useState<boolean | null>(null)


  async function authToken(): Promise<string> {
    const token = getSessionCookieToken() ?? (await getToken())
    if (!token) throw new Error('Not authenticated')
    return token
  }

  async function fetchCatalog() {
    setCatalogLoading(true)
    setCatalogError(null)
    try {
      const token = await authToken()
      const saas = await api.getCatalog(token)
      setCatalog(saas)
      try {
        const cliResp = await api.getCliCatalog(token)
        setCliCatalog(cliResp.cli)
      } catch {
        setCliCatalog([])
      }
    } catch (err) {
      setCatalogError((err as Error).message)
    } finally {
      setCatalogLoading(false)
    }
  }

  async function fetchHealth() {
    setHealthLoading(true)
    setHealthError(null)
    try {
      const token = await authToken()
      const resp = await api.getCredentialsHealth(token)
      setHealth(resp.integrations)
    } catch (err) {
      setHealthError((err as Error).message)
    } finally {
      setHealthLoading(false)
    }
  }

  useEffect(() => {
    fetchCatalog()
    fetchHealth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saasByCategory = useMemo(() => groupByCategory(catalog), [catalog])
  const cliByCategory = useMemo(() => groupByCategory(cliCatalog), [cliCatalog])

  // Count connected integrations
  const connectedCount = useMemo(() => {
    return Object.values(health).filter((s) => s === 'ok').length
  }, [health])

  return (
    <div className="page">
      {/* ── Page Header ────────────────────────────────────────── */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="text-display-md" style={{ margin: 0 }}>Integrations</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
            Connect your tools so SelfHeal can investigate and fix incidents.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span className="badge badge-success" style={{ padding: '4px 12px' }}>
            {connectedCount} Connected
          </span>
          <button className="page-refresh-btn" onClick={fetchHealth} disabled={healthLoading}>
            {healthLoading ? 'Checking…' : 'Refresh Status'}
          </button>
        </div>
      </div>

      {/* ── Security Trust Notice Banner ───────────────────────── */}
      <div className="security-banner">
        <div className="security-banner-icon-container">
          <svg
            className="security-banner-svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="rgba(52, 211, 153, 0.08)" />
            <path d="m9 11 2 2 4-4" />
          </svg>
        </div>
        <div className="security-banner-content">
          <h4>Your data stays private</h4>
          <p>
            Credentials are encrypted with <strong>AES-256</strong> and stored in{' '}
            <strong>AWS Secrets Manager</strong>. SelfHeal queries your tools in read-only mode
            and never stores your logs or data outside your tenant.
          </p>
        </div>
      </div>


      {catalogError && (
        <div className="state-msg error">Could not load integration catalog: {catalogError}</div>
      )}
      {healthError && (
        <div className="state-msg error">Health check failed: {healthError}</div>
      )}

      {/* ── Catalog sections wrapped with blur+spinner overlay ── */}
      <LoadingOverlay loading={catalogLoading}>

        {/* ── AI Provider Keys (BYOK) — only shown when feature flag is on ── */}
        {byokFeatureEnabled !== false && (
          <section className="integration-section">
            <h2 className="integration-section-title">AI Provider Keys</h2>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginTop: '2px' }}>
                <IntegrationLogo integrationId="openai" size={24} />
                <IntegrationLogo integrationId="anthropic" size={24} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px', color: 'var(--text-h)', fontSize: '13.5px', fontWeight: 600 }}>Use your own AI key (BYOK)</h4>
                <p className="settings-intro" style={{ margin: 0, color: 'var(--text)', fontSize: '13px', lineHeight: '1.5' }}>
                  Provide your own Anthropic, OpenAI, or Groq API key. SelfHeal will use it for investigations and code-fix suggestions instead of the default key.
                </p>
              </div>
            </div>
            <div className="cred-list">
              <ByokSettings isAdmin={isAdmin} onFeatureEnabled={setByokFeatureEnabled} />
            </div>
          </section>
        )}

        {/* ── Categorized Integrations Grid ─────────────────────── */}
        {[...saasByCategory.entries()].map(([category, entries]) => (
          <section key={category} className="integration-section">
            <h2 className="integration-section-title">{category}</h2>
            <div className="cred-list">
              {entries.map((entry) =>
                entry.id === 'github' ? (
                  <GitHubAppCard key={entry.id} onStatusChange={fetchHealth} />
                ) : entry.id === 'bitbucket' ? (
                  <BitbucketAppCard key={entry.id} onStatusChange={fetchHealth} />
                ) : (
                  <SaasIntegrationCard
                    key={entry.id}
                    entry={entry}
                    health={health[entry.id]}
                    onSaved={fetchHealth}
                  />
                ),
              )}
            </div>
          </section>
        ))}

        {/* ── Upcoming Integrations ─────────────────────────────── */}
        {cliCatalog.length > 0 && (
          <section className="integration-section integration-section-cli">
            <h2 className="integration-section-title">Upcoming Integrations</h2>
            <p className="settings-intro settings-intro-compact" style={{ marginBottom: '12px' }}>
              These integrations are on our roadmap. Dashboard setup will be available soon.
            </p>
            {[...cliByCategory.entries()].map(([category, entries]) => (
              <div key={category} className="cli-category-block" style={{ marginBottom: '16px' }}>
                <h3 className="cli-category-title" style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px' }}>{category}</h3>
                <div className="cli-integration-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {entries.map((entry) => (
                    <CliIntegrationRow key={entry.id} entry={entry} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </LoadingOverlay>


    </div>
  )
}
